import { useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { SelectField } from "@/components/SelectField";
import { buttonClass } from "@/components/buttonStyles";
import {
  WonBidCheckbox,
  WonBidUploadSlot,
} from "@/components/bond-submission/WonBidUploadSlot";
import { ApiError } from "@/lib/api";
import {
  BOND_FORM_SECTIONS,
  BOND_FORM_SECTION_LABELS,
  bondFormStatusLine,
  confirmBondForm,
  formsBySection,
  kindsForSection,
  uploadBondForm,
  type BondForms,
  type BondFormSection,
} from "@/lib/bondForms";
import { screenDocument, type DocumentRules } from "@/lib/brokerDocumentTypes";
import { messageFor } from "@/lib/errors";
import { PAYMENT_ALONGSIDE, PERFORMANCE_ALWAYS } from "@/lib/wonBid";

// BRK-S421 step 3, "Confirm bond forms". First the bonds requested
// (BRK-S327: the Performance bond is fixed, the Payment bond the one
// choice), then, per section, the forms preset for this bond
// (PMS-S848) with Confirm, and an upload of the obligee's own
// form with its type chosen from PMS's served kinds (a select, D-10).
//
// NOTHING IS OPTIMISTIC (NN #7): a form reads Confirmed or Uploaded only from
// the 2xx's refreshed list, which replaces the stepper's; a refusal renders
// on the row or the section, and the choices made stay.

export const FORMS_UNREADABLE =
  "We couldn’t load the bond forms for this request. You can still finish the won bid.";

export function WonBidFormsStep({
  orderId,
  heading: H,
  payment,
  onPayment,
  bondsError,
  forms,
  formsError,
  rules,
  accept,
  onForms,
  onBusy,
}: {
  orderId: string;
  heading: "h3" | "h4";
  payment: boolean;
  onPayment: (on: boolean) => void;
  bondsError?: string;
  /** The read's answer; null while it is read or when it failed. */
  forms: BondForms | null;
  formsError: boolean;
  rules: DocumentRules | null;
  accept: string;
  /** A 2xx's refreshed list. */
  onForms: (forms: BondForms) => void;
  /** A confirm or an upload in flight: the stepper waits on it. */
  onBusy: (busy: boolean) => void;
}) {
  const { authedRequest, authedUpload } = useAuth();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [kinds, setKinds] = useState<Partial<Record<BondFormSection, string>>>(
    {},
  );
  const [uploading, setUploading] = useState<BondFormSection | null>(null);
  const [uploadErrors, setUploadErrors] = useState<
    Partial<Record<BondFormSection, string>>
  >({});

  const refusal = (err: unknown) =>
    err instanceof ApiError
      ? messageFor(err)
      : "That didn’t go through. Please try again.";

  async function confirm(formId: string) {
    setConfirming(formId);
    setRowErrors((prev) => ({ ...prev, [formId]: "" }));
    onBusy(true);
    try {
      onForms(await confirmBondForm(authedRequest, orderId, formId));
    } catch (err) {
      setRowErrors((prev) => ({ ...prev, [formId]: refusal(err) }));
    } finally {
      setConfirming(null);
      onBusy(false);
    }
  }

  async function upload(section: BondFormSection, file: File) {
    const kind = kinds[section];
    setUploadErrors((prev) => ({ ...prev, [section]: undefined }));
    if (!kind) {
      setUploadErrors((prev) => ({
        ...prev,
        [section]: "Choose the form type first.",
      }));
      return;
    }
    const rejection = rules ? screenDocument(file, rules) : null;
    if (rejection) {
      setUploadErrors((prev) => ({ ...prev, [section]: rejection.message }));
      return;
    }
    setUploading(section);
    onBusy(true);
    try {
      onForms(
        await uploadBondForm(authedUpload, orderId, { file, section, kind }),
      );
      setKinds((prev) => ({ ...prev, [section]: undefined }));
    } catch (err) {
      setUploadErrors((prev) => ({ ...prev, [section]: refusal(err) }));
    } finally {
      setUploading(null);
      onBusy(false);
    }
  }

  const grouped = forms ? formsBySection(forms.forms) : null;
  return (
    <>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-2 text-sm font-semibold text-brk-text">
          Bonds requested
        </legend>
        {/* Not a choice (BRK-S327): a Performance bond is always issued on
            a won bid. A disabled, checked box says so to every reader. */}
        <WonBidCheckbox
          label="Performance bond"
          checked
          disabled
          note={PERFORMANCE_ALWAYS}
        />
        <WonBidCheckbox
          label="Include a Payment bond"
          checked={payment}
          note={PAYMENT_ALONGSIDE}
          onChange={onPayment}
        />
        {bondsError && (
          <p role="alert" className="text-sm text-brk-danger">
            {bondsError}
          </p>
        )}
      </fieldset>

      {formsError && (
        <p role="alert" className="text-sm text-brk-danger">
          {FORMS_UNREADABLE}
        </p>
      )}
      {!formsError && forms === null && (
        <p role="status" className="text-sm text-brk-dim">
          Loading the bond forms…
        </p>
      )}
      {forms !== null &&
        grouped !== null &&
        BOND_FORM_SECTIONS.map((section) => {
          const label = BOND_FORM_SECTION_LABELS[section];
          const options = kindsForSection(forms.kinds, section);
          const kindLabel = (kind: string) =>
            forms.kinds.find((k) => k.value === kind)?.label ?? null;
          return (
            <section
              key={section}
              aria-label={label}
              className="flex min-w-0 flex-col gap-3 border-t border-brk-border pt-4"
            >
              <H className="text-sm font-semibold text-brk-text">{label}</H>
              {grouped[section].length === 0 ? (
                <p className="text-sm text-brk-dim">
                  No forms are preset for this section.
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {grouped[section].map((form) => (
                    <li
                      key={form.id}
                      className="flex min-w-0 flex-col gap-2 rounded-md border border-brk-border p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 flex-col">
                        <span className="break-words text-sm font-medium text-brk-text">
                          {form.title}
                        </span>
                        <span className="break-words text-sm text-brk-dim">
                          {[kindLabel(form.kind), bondFormStatusLine(form)]
                            .filter((part): part is string => !!part)
                            .join(" · ")}
                        </span>
                        {rowErrors[form.id] && (
                          <span
                            role="alert"
                            className="text-sm text-brk-danger"
                          >
                            {rowErrors[form.id]}
                          </span>
                        )}
                      </div>
                      {form.status === "preset" && (
                        <button
                          type="button"
                          onClick={() => void confirm(form.id)}
                          disabled={confirming !== null}
                          aria-label={`Confirm ${form.title}`}
                          className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start sm:self-center`}
                        >
                          {confirming === form.id ? "Confirming…" : "Confirm"}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {options.length > 0 && (
                <div className="flex min-w-0 flex-col gap-2">
                  <SelectField
                    label={`${label} form type`}
                    placeholder="Select a form type"
                    value={kinds[section] ?? ""}
                    options={options}
                    onChange={(e) => {
                      const next = e.target.value;
                      setKinds((prev) => ({ ...prev, [section]: next }));
                      setUploadErrors((prev) => ({
                        ...prev,
                        [section]: undefined,
                      }));
                    }}
                  />
                  <WonBidUploadSlot
                    inputLabel={`Upload a ${label} form`}
                    prompt="Missing a form? Drag & drop the obligee’s form, or"
                    hint="Choose its type above first."
                    accept={accept}
                    held={null}
                    uploading={uploading === section}
                    disabled={!kinds[section] || uploading !== null}
                    error={uploadErrors[section]}
                    onFile={(file) => void upload(section, file)}
                  />
                </div>
              )}
            </section>
          );
        })}
    </>
  );
}
